import React, {useCallback, useState, useRef, useEffect} from 'react';
import PropTypes from 'prop-types';
import css from './LogoUpload.module.scss';
import ImageAdd from "../../Icons/ImageAdd.jsx";

const LogoUpload = ({
  onImageChange,
  initialImage,
  logoText = 'Hotel Logo',
  label,
  required,
  error,
}) => {
  const [image, setImage] = useState(initialImage);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (typeof initialImage.preview === 'string') {
      setImage(initialImage);
    }
  }, [initialImage])

  const handleFile = useCallback((file) => {
    if (!file) return;
    
    if (file.type !== 'image/png' && file.type !== 'image/jpeg' && file.type !== 'image/jpg') {
      return;
    }

    const preview = URL.createObjectURL(file);
    const newImage = {
      file: file,
      preview: preview
    };
      
    setImage(newImage);
    onImageChange?.(file);
  }, [onImageChange]);

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback((e) => {
    handleFile(e.target.files[0]);
  }, [handleFile]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.dataTransfer.files?.length) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, [handleFile]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  return (
    <div className={css.logoUpload}>
      {label &&
        <label className={css.label}>
          {label}
          {required && <span className={css.required}>*</span>}
        </label>
      }
      <div className={css.uploadSection}>
        <div className={css.preview}>
          {image?.preview ?
            <img
              src={image.preview}
              alt="Hotel logo preview"
              className={css.logoImage}
            />
          :
            <div className={css.placeholder}>
              {logoText}
            </div>
          }
        </div>
        <div
          className={`${css.dropzone} ${error ? css.error : ''}`}
          onClick={handleClick}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          data-error={!!error}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg"
            onChange={handleFileInputChange}
            style={{display: 'none'}}
          />
          <div className={css.content}>
            <ImageAdd width={24} color="var(--primary-color)"/>
            <div className={css.uploadText}>
              <span className={css.clickText}>Click to upload</span> or drag and drop
              <div className={css.supportText}>PNG or JPG (max. 1000x1000px)</div>
            </div>
          </div>
        </div>
      </div>
      {error && <span className={css.errorMessage}>{error}</span>}
    </div>
  );
};

LogoUpload.propTypes = {
  onImageChange: PropTypes.func.isRequired,
  initialImage: PropTypes.shape({
    preview: PropTypes.string.isRequired
  }),
  error: PropTypes.string
};

export default LogoUpload;
